import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, m } from 'motion/react';
import { Check, ChevronDown, Search } from 'lucide-react';
import type { CategoryNode, FrequentCategory } from '@lira/shared';
import { apiGet } from '@/lib/api';
import { cn } from '@/lib/cn';
import { CategoryIcon } from '@/components/ui';

const EASE = [0.16, 1, 0.3, 1] as const;

interface FlatCategory {
  id: string;
  name: string;
  color: string;
  icon: string;
  isSystem: boolean;
  /** nome do pai, p/ dar contexto às subcategorias na lista. */
  parentName: string | null;
}

function flatten(nodes: CategoryNode[]): FlatCategory[] {
  const out: FlatCategory[] = [];
  for (const node of nodes) {
    out.push({
      id: node.id,
      name: node.name,
      color: node.color,
      icon: node.icon,
      isSystem: node.isSystem,
      parentName: null,
    });
    for (const child of node.children) {
      out.push({
        id: child.id,
        name: child.name,
        color: child.color,
        icon: child.icon,
        isSystem: child.isSystem,
        parentName: node.name,
      });
    }
  }
  return out;
}

interface CategoryPickerProps {
  type: 'income' | 'expense';
  value: string | null;
  onChange: (id: string) => void;
}

/** Chips de categorias frequentes (1 toque) + "mais…" que expande a árvore completa buscável. */
export function CategoryPicker({ type, value, onChange }: CategoryPickerProps) {
  const [treeOpen, setTreeOpen] = useState(false);
  const [search, setSearch] = useState('');

  const frequentQuery = useQuery({
    queryKey: ['categories', 'frequent', type],
    queryFn: () =>
      apiGet<{ categories: FrequentCategory[] }>(`/categories/frequent?type=${type}&days=90`),
  });

  const treeQuery = useQuery({
    queryKey: ['categories', type],
    queryFn: () => apiGet<{ categories: CategoryNode[] }>(`/categories?type=${type}`),
  });

  const flat = useMemo(
    () => (treeQuery.data ? flatten(treeQuery.data.categories) : []),
    [treeQuery.data],
  );

  const frequent = frequentQuery.data?.categories ?? [];
  const selected =
    flat.find((c) => c.id === value) ??
    frequent
      .filter((c) => c.id === value)
      .map((c) => ({ ...c, isSystem: false, parentName: null }) as FlatCategory)[0] ??
    null;

  // Chips: a selecionada primeiro, depois as frequentes (sem duplicar a selecionada).
  const chipList: FlatCategory[] = [];
  if (selected) chipList.push(selected);
  for (const c of frequent) {
    if (c.id !== value) {
      chipList.push({ ...c, isSystem: false, parentName: null });
    }
  }

  const filtered = search.trim()
    ? flat.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : flat;

  const pick = (id: string) => {
    onChange(id);
    setTreeOpen(false);
    setSearch('');
  };

  return (
    <div>
      <label className="mb-1.5 block text-[13px] text-muted">Categoria</label>

      <div className="flex flex-wrap gap-2">
        {chipList.slice(0, 6).map((c) => {
          const active = c.id === value;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onChange(c.id)}
              className={cn(
                'flex items-center gap-1.5 rounded-full border py-1 pl-1 pr-3 text-[13px] font-medium transition-colors',
                active
                  ? 'border-primary bg-primary-weak text-primary'
                  : 'border-border text-ink hover:border-muted',
              )}
            >
              <CategoryIcon icon={c.icon} color={c.color} size={22} />
              <span className="max-w-[9rem] truncate">
                {c.name}
                {c.isSystem && '*'}
              </span>
              {active && <Check className="size-3.5 shrink-0" />}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => setTreeOpen((v) => !v)}
          className={cn(
            'flex items-center gap-1 rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors',
            treeOpen
              ? 'border-primary bg-primary-weak text-primary'
              : 'border-border text-muted hover:border-muted hover:text-ink',
          )}
        >
          mais…
          <ChevronDown className={cn('size-3.5 transition-transform', treeOpen && 'rotate-180')} />
        </button>
      </div>

      <AnimatePresence initial={false}>
        {treeOpen && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.18, ease: EASE }}
            className="overflow-hidden"
          >
            <div className="mt-2 rounded-xl border border-border bg-surface p-2">
              <div className="flex h-10 items-center gap-2 rounded-lg bg-canvas px-3">
                <Search className="size-4 shrink-0 text-muted" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar categoria"
                  autoComplete="off"
                  className="w-full bg-transparent text-[15px] text-ink outline-none placeholder:text-muted"
                />
              </div>

              <ul className="mt-1 max-h-60 overflow-auto">
                {treeQuery.isLoading && (
                  <li className="px-3 py-6 text-center text-sm text-muted">Carregando…</li>
                )}
                {treeQuery.isSuccess && filtered.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-muted">
                    Nenhuma categoria encontrada.
                  </li>
                )}
                {filtered.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => pick(c.id)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-canvas',
                        c.parentName && 'pl-6',
                      )}
                    >
                      <CategoryIcon icon={c.icon} color={c.color} size={26} />
                      <span className="min-w-0 flex-1 truncate text-[15px] text-ink">
                        {c.name}
                        {c.isSystem && '*'}
                        {c.parentName && (
                          <span className="ml-1.5 text-[12px] text-muted">· {c.parentName}</span>
                        )}
                      </span>
                      {c.id === value && <Check className="size-4 shrink-0 text-primary" />}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  );
}
